import { useState } from 'react';
import moment from 'moment';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import useIsMobile from '../../hooks/useIsMobile';
import { 
  getLocationOptions, 
  getLocationColor 
} from '../../helpers/locations';
import CustomDialog from '../common/CustomDialog';
import CalendarDayView from './CalendarDayView';
import CalendarWeekView from './CalendarWeekView';
import SessionForm from '../sessions/SessionForm';
import SelectPatientForm from '../scheduling/SelectPatientForm';
import VacationTimeDialog from '../scheduling/VacationTimeDialog';
import { getAwayTimeBlocks, getAvailabilityBlocks } from './calendarUtils';
import useError from '../../hooks/useError';
import { useCreateVacationTimeMutation } from '../../redux/services/vacationTimeApi';

const handleSubmit = ({
  createVacationTime,
  vacationTimesQuery,
  setVacationDialogIsOpen,
  setError,
}) => formData => {
  const response = createVacationTime(formData);
  if (response.error) return setError({message: 'Error creating vacation time'});
  setError(null);
  setVacationDialogIsOpen(false);
};

const CalendarScreen = ({
  date,
  sessions,
  awayTimes = [],
  availabilities = [],
  calendarView = 'week',
  metrics = [],
  boxStyles = {
    backgroundColor: 'white',
    // overflowY: 'hidden',
    borderRadius: 3,
    boxShadow: 1,
    border: 1,
    borderColor: 'divider',
  },
  onDateChange = (e, date) => console.log('Change date', date),
  onCalendarViewChange = (e, view) => console.log('Change calendar view', view),
} = {}) => {
  const isMobile = useIsMobile();
  const [sessionId, setSessionId] = useState(null);
  const [vacationTimeDialogOpen, setVacationTimeDialogOpen] = useState(false);
  const [createSessionDialogOpen, setCreateSessionDialogOpen] = useState(false);
  const error = useError();
  const [createVacationTime] = useCreateVacationTimeMutation();
  const selectedSession = sessions?.find(s => s.id === sessionId);
  const awayTimeBlocks = (
    awayTimes && awayTimes.length
    ? getAwayTimeBlocks({
        timeStart: moment(date).startOf(calendarView).toISOString(),
        timeEnd: moment(date).endOf(calendarView).toISOString(),
        startHour: 8,
        endHour: 20
      })(awayTimes)
    : []
  );
  const availabilityBlocks = (
    availabilities && availabilities.length
    ? getAvailabilityBlocks({ date, startHour: 8, endHour: 20 })(availabilities)
    : []
  );
  const onSubmitVacationTime = handleSubmit({
    createVacationTime,
    setVacationDialogIsOpen: setVacationTimeDialogOpen,
    setError: error.create,
  });
  return (
    <Box style={{paddingLeft: isMobile ? '0em' : '1em', paddingBottom: '1em'}}>
      <Box style={{paddingBottom: '2em'}}>
        <Grid container>
          <Grid item xs={12} md={9} spacing={2}>
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Stack direction='row' spacing={1}>
                <Button
                  color='primary'
                  variant='contained'
                  size='small'
                  onClick={() => setCreateSessionDialogOpen(true)}
                >
                  <AddIcon/>
                  Schedule
                </Button>
                <Button 
                  color='info'
                  variant='contained'
                  size='small'
                  onClick={() => setVacationTimeDialogOpen(true)}
                >
                  Away Time
                </Button>
                <ButtonGroup>
                  <Button
                    size='small'
                    variant={calendarView === 'week' ? 'contained' : 'outlined'}
                    color='primary'
                    onClick={e => onCalendarViewChange(e, 'week')}
                  >
                    Week
                  </Button>
                  <Button
                    size='small'
                    variant={calendarView === 'day' ? 'contained' : 'outlined'}
                    color='primary'
                    onClick={e => onCalendarViewChange(e, 'day')}
                    style={{minWidth: '60px', fontSize: '0.875rem'}}
                  >
                    Day
                  </Button>
                </ButtonGroup>
              </Stack>
            </Stack>

          </Grid>

        </Grid>
      </Box>
      <Grid container spacing={1} sx={{ml: {xs: -1}}}>
        <Grid item sx={12} md={9}>
          {calendarView === 'week' && 
            <CalendarWeekView
              date={date}
              sessions={sessions}
              awayTimeBlocks={awayTimeBlocks}
              availabilityBlocks={availabilityBlocks}
              boxStyles={boxStyles}
              onDateChange={onDateChange}
              onEditSession={(e, id) => setSessionId(id)}
            />
          }
          {
            calendarView === 'day' &&
            <CalendarDayView
              date={date}
              sessions={sessions}
              awayTimeBlocks={awayTimeBlocks}
              availabilityBlocks={availabilityBlocks}
              boxStyles={boxStyles}
              onDateChange={onDateChange}
              onEditSession={(e, id) => setSessionId(id)}
            />
          }
        </Grid>
        {/* hide on mobile */}
        <Grid item sx={{display: {xs: 'none', sm: 'none', md: 'flex'}}} md={3}>
          <Stack spacing={2} style={{width: '100%'}}>
            <Box sx={{...boxStyles, p: 2}}>
              <Typography variant='h2'>Location Types</Typography>
              <Stack spacing={0.5} pt={1}>
                {getLocationOptions().map(loc => (
                  <Stack direction='row' spacing={1}>
                    <Box
                      sx={{
                        width: 20, 
                        height: 20, 
                        backgroundColor: getLocationColor(loc.value),
                        borderRadius: 3,
                      }}
                    />
                    <Typography variant='body1'>{loc?.emoji || null} {loc.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box sx={{...boxStyles, p: 2}}>
              <Typography variant='h2'>This Week</Typography>
              <Stack spacing={2} pt={1}>
                {metrics.map(metric => 
                  <Stack direction='row' spacing={1}>
                    <Typography variant='h1' style={{color: config().colors.secondary}}>
                      {metric.value}
                    </Typography>
                    <Typography variant='body' style={{paddingTop: '0.5em'}}>{metric.label}</Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <CustomDialog
        open={!!sessionId}
        onClose={() => setSessionId(null)}
        fullWidth={true}
        maxWidth='md'
        showCloseButton={false}
      >
        {
          selectedSession
          ? <SessionForm
            session={selectedSession}
            isPatient={false}
            onClose={() => setSessionId(null)}
            onSubmitted={() => setSessionId(null)}
          />
          : <></>
        }
      </CustomDialog>
      <VacationTimeDialog 
        isOpen={vacationTimeDialogOpen}
        onClose={() => setVacationTimeDialogOpen(false)}
        onSubmit={onSubmitVacationTime}
      />
      <CustomDialog
        open={createSessionDialogOpen}
        onClose={() => setCreateSessionDialogOpen(false)}
        fullWidth={true}
        maxWidth='sm'
      >
        <SelectPatientForm title='Find a Patient' />
      </CustomDialog>
    </Box>
  );
};

export default CalendarScreen;